import type { EvalCaseSnapshot } from '../types/evalTypes';

interface CasePickerProps {
  cases: EvalCaseSnapshot[];
  selected: Set<string>;
  disabled: boolean;
  onChange: (selected: Set<string>) => void;
}

/** All cases are selected by default; a subset is for quick checks while editing a prompt. */
export const CasePicker = ({ cases, selected, disabled, onChange }: CasePickerProps) => {
  const toggle = (caseId: string) => {
    const next = new Set(selected);
    if (next.has(caseId)) {
      next.delete(caseId);
    } else {
      next.add(caseId);
    }
    onChange(next);
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200">
        <span className="text-sm font-medium text-gray-700">
          Cases ({selected.size} of {cases.length})
        </span>
        <div className="flex gap-3 text-xs">
          <button
            type="button"
            disabled={disabled}
            className="text-blue-600 hover:underline disabled:text-gray-400"
            onClick={() => onChange(new Set(cases.map((c) => c.id)))}
          >
            Select all
          </button>
          <button type="button" disabled={disabled} className="text-blue-600 hover:underline disabled:text-gray-400" onClick={() => onChange(new Set())}>
            Select none
          </button>
        </div>
      </div>
      <ul className="p-3 space-y-2 max-h-[28rem] overflow-y-auto">
        {cases.map((evalCase) => (
          <li key={evalCase.id}>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.has(evalCase.id)}
                disabled={disabled}
                onChange={() => toggle(evalCase.id)}
              />
              <span>
                <span className="font-mono text-xs text-gray-900">{evalCase.id}</span>
                {evalCase.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${tag === 'risk' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {tag}
                  </span>
                ))}
                <span className="block text-xs text-gray-500">{evalCase.description}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};
